/* eslint-disable @typescript-eslint/no-explicit-any */
// src/redux/slices/portCreationSlice.ts
import { RootState } from "@redux/store/store";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { fetchConnectionCapacityApi, fetchMasksApi, fetchPortAndLagsApi } from "kontrolEdge/components/Internet/requests/request";
import { fetchLocationsApi, fetchPricingApi, fetchPricingCrossConnectApi } from "kontrolEdge/components/Port/requests/request";

const API_URL = process.env.REACT_APP_API_URL;
// Define types for the port creation payload
interface Partner {
	id: string;
	name: string;
}

interface Customer {
	id: string;
	name: string;
}
interface AvailablePort {
	id: number;
	name: string;
	mbps_capacity: number;
	datacenter: string;
  }
  
  interface AvailableLag {
	id: number;
	name: string;
	mbps_capacity: number;
  }
  

interface Status {
	current: "draft" | "pending" | "confirmed" | "cancelled";
	lastUpdate?: string;
}

interface FetchCrossPricingArgs {
	token: string;
	provider: string;
  }
interface FetchArgs {
token: string;
}
  interface CrossPricingData {
	currency: string;
	external_name: string;
	name: string;
	period_unit: string;
	plan_id: string;
	price: number;
  }
  
  interface FetchPricingArgs {
	token: string;
  }

export interface Port {
	portId?: string;
	basicInfo?: {
		name: string;
		tag?: string;
	};
	location?: {
		datacenterId: string;
		facilityName: string;
	};
	configuration?: {
		capacity?: {
			speed: string;
			type: "single" | "dual";
		};
		term?: {
			months: number;
			autoRenew?: boolean;
		};
		vlan?: string;
		automaticVlan?: boolean;
		crossConnect?: string;
		crossConnectPricing?: string;
		address?: {
			street: string;
			city: string;
			state: string;
			country: string;
			postalCode: string;
		};
	};
	pricing?: {
		totalMRC: number;
		currency?: string;
	};
	internetConnection?: InternetConnection;
}

export interface InternetConnection {
	// Optional redundancy: if true, indicates that internet redundancy is enabled for 2 ports at the same location.
	redundancyEnabled?: boolean;
	// Connection capacity, e.g., "500mbps", "1gbps"
	connectionCapacity: string;
	// Public IP addressing configuration
	publicIPs: {
	byCountry: string;
	byBlock: number;
	};
	// Contract term for the internet service
	contractTerm: {
	months: number;
	autoRenew?: boolean;
	};
	// Connector description related to the selected port
	connectorDescription: string;
	// Optional fields for additional tagging or port naming related to the internet service
	tag?: string;
	portName?: string;
	portTag?: string;
	// VLAN configuration: if automatic is false, a manual VLAN input can be provided
	vlanConfiguration: {
	automatic: boolean;
	manualVlan?: string;
	};
  }

export interface Location {
	datacenterId: string;
	name: string;
	displayName: string;
	direction: string;
	port_available?: {
	dual?: {
		"10gbps": boolean;
		"100gbps": boolean;
	}
	single?: {
		"10gbps": boolean;
		"100gbps": boolean;
	}
	};
  }
  
  

export interface Order {
	orderId?: string;
	name: string;
	partner?: Partner;
	customer?: Customer;
	status: Status;
	ports: Port[];
	crossPricing?: CrossPricingData | null;
	locations: Location[];
	termsAccepted?: boolean;
	selectedPortIndex?: number;
	currentStep: number;
	pricingPlans?: Record<string, any> | null;
	email: string; 
	isEditing?: boolean;
	// New fields for available ports and lags
	availablePorts?: AvailablePort[];
	availableLags?: AvailableLag[];
}

// Async thunk to update the port
export const updatePortAsync = createAsyncThunk(
	"portCreation/updatePortAsync",
	async (
		{ index, portData }: { index: number; portData: Partial<Port> },
		thunkAPI
	) => {
		try {
			const response = await axios.put(`/api/ports/${portData}`, portData);
			return { index, updatedPort: response.data };
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

// Async thunk to update the port
export const saveOrderAsync = createAsyncThunk(
	"portCreation/saveOrderAsync",
	async (
	{ token, orderName }: { token: string; orderName: string },
	thunkAPI
) => {
	try {
	// Retrieve the order state
	const state = thunkAPI.getState() as RootState;
	const orderState = state.order;
	
	// Build the order payload using the provided orderName
	const orderPayload = {
		id: orderState.orderId || "generated-id", // Optionally generate or use an existing id
		name: orderName,
		data: orderState.ports, // Wrap ports under "data"
		email: orderState.email || "dc412l@transitxchange.cloud",
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	};

	
	const response = await axios.post(
		`${API_URL}/v1/kontroledge/saveorder`,
		orderPayload,
		{
		headers: { Authorization: `Bearer ${token}` },
		}
	);
	return response.data;
	} catch (error) {
	return thunkAPI.rejectWithValue(error);
	}
}
  );
  
//Saved to validate later:
export const createPortAsync = createAsyncThunk(
	"portCreation/createPortAsync",
	async (token: string, thunkAPI) => {
		try {
			// Retrieve the port data from the Redux state:
			const state = thunkAPI.getState() as RootState;
			const orderState = state.order;
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const orderPayload = {
				name: orderState.name || "orden_test",
				data: orderState.ports,
			};
			// const portsPayload = state.order.ports; // This is an array

			// if (!portsPayload || portsPayload.length === 0) {
			// 	throw new Error("No ports available to create an order");
			// }
			// const response = await axios.post(
			// 	`${API_URL}/v1/kontroledge/order/ports?tenantId=80`,
			// 	orderPayload,
			// 	{
			// 		headers: { Authorization: `Bearer ${token}` },
			// 	}
			// );
			// return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);
//--------Step 1 in order creation
export const fetchLocations = createAsyncThunk(
	"order/fetchLocations",
	async (token: string, thunkAPI) => {
		try {
			const response = await fetchLocationsApi(token);
			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);
//This pricing is for the step 1 in order creation
export const fetchPricingPlans = createAsyncThunk(
	"order/fetchPricingPlans",
	async ({ token }: FetchPricingArgs, thunkAPI) => {
		try {
			const response = await fetchPricingApi(token);
			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
);

//--------Step 2 in order creation
export const fetchCrossPricing = createAsyncThunk(
	"order/fetchCrossPricing",
	async ({ token, provider }: FetchCrossPricingArgs, thunkAPI) => {
		try {
			const response = await fetchPricingCrossConnectApi(token, provider);
			return response.data;
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	}
  );

//--------Step 1 in connection creation
export const fetchPortsAndLags = createAsyncThunk(
	"order/fetchPortsAndLags",
	async ({ token }: FetchArgs,  thunkAPI) => {
	try {
	const response = await fetchPortAndLagsApi(token);
	return response.data;
	} catch (error) {
	return thunkAPI.rejectWithValue(error);
	}
	}
  );

  export const fetchMasks = createAsyncThunk(
	"order/fetchMasks",
	async ({ token }: FetchArgs, thunkAPI) => {
	try {
	const response = await fetchMasksApi(token);
	return response.data;
	} catch (error) {
	return thunkAPI.rejectWithValue(error);
	}
	}
  );

  export const fetchConnectionCapacity = createAsyncThunk(
	"order/fetchConnectionCapacity",
	async ({ token }: FetchArgs, thunkAPI) => {
	try {
	const response = await fetchConnectionCapacityApi(token);
	return response.data;
	} catch (error) {
	return thunkAPI.rejectWithValue(error);
	}
	}
  );
  


// Helper function to lookup facilityName from locations using datacenterId
function getFacilityName(state: Order, datacenterId: string): string {
	const loc = state.locations?.find(
		(location) => location.datacenterId === datacenterId
	);
	return loc ? loc.name : "";
}

// Initial State
const initialState: Order = {
	status: {
		current: "draft",
	},
	name: "",
	email: "",
	ports: [],
	locations: [],
	selectedPortIndex: 0,
	currentStep: 1,
	isEditing: false,
	availablePorts: [],
	availableLags: [],
};

// Action Payload Type
type UpdateOrderPayload = Partial<Order> & {
	portIndex?: number;
	portData?: Partial<Port>;
};

// Slice
const orderSlice = createSlice({
	name: "order",
	initialState,
	reducers: {
		//Unified Action to Update Order or Ports
		updateOrder(state, action: PayloadAction<UpdateOrderPayload>) {
			const { portIndex, portData, ...orderData } = action.payload;
			Object.assign(state, orderData);
			if (portIndex !== undefined && portData) {
				// If the payload includes location data, enrich it if facilityName is missing
				if (
					portData.location &&
					!portData.location.facilityName &&
					portData.location.datacenterId
				) {
					portData.location.facilityName = getFacilityName(
						state,
						portData.location.datacenterId
					);
				}
				if (state.ports[portIndex]) {
					// If there's configuration data in both the existing port and the new portData,
					// merge them so that the new data doesn't override the existing fields.
					if (portData.configuration && state.ports[portIndex].configuration) {
						state.ports[portIndex].configuration = {
							...state.ports[portIndex].configuration,
							...portData.configuration,
						};
					} else if (portData.configuration) {
						// If there is configuration data in the new payload but none in the existing port.
						state.ports[portIndex].configuration = {
							...portData.configuration,
						};
					}
					// Merge the rest of the portData properties.
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { configuration, ...rest } = portData;
					Object.assign(state.ports[portIndex], rest);
				} else {
					state.ports[portIndex] = { ...portData };
				}
			}
		},
		loadSavedOrder: (state, action) => {
			// Replace current state with the saved order data
			return { ...state, ...action.payload };
		},
		setPricingPlans: (
			state,
			action: PayloadAction<Record<string, any>>
		) => {
			state.pricingPlans = action.payload;
		},
		setSelectedPort(state, action: PayloadAction<number>) {
			if (action.payload >= 0 && action.payload < state.ports.length) {
				state.selectedPortIndex = action.payload;
			}
		},
		setCrossConnectPricing: (state, action: PayloadAction<string>) => {
			const portIndex = state.selectedPortIndex ?? 0;
			if (state.ports[portIndex]) {
			state.ports[portIndex] = {
			...state.ports[portIndex],
			configuration: {
				...state.ports[portIndex].configuration,
				crossConnectPricing: action.payload,
			},
			};
		}
		},
		//Add New Port
		addNewPort(state, action: PayloadAction<Port>) {
			state.ports.push(action.payload);
		},
		//Remove Port
		removePort(state, action: PayloadAction<number>) {
			state.ports.splice(action.payload, 1);
		},
		//Reset Order
		resetOrder(state) {
			Object.assign(state, initialState);
		},
		setCurrentStep(state, action: PayloadAction<number>) {
			state.currentStep = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			fetchLocations.fulfilled,
			(state, action: PayloadAction<{ data: Location[] }>) => {
				state.locations = action.payload.data;
			}
		);
		builder.addCase(fetchLocations.rejected, (state, action) => {
			// Handle errors here
			console.error("Failed to fetch locations:", action.payload);
		});
		builder.addCase(fetchCrossPricing.fulfilled, (state, action) => {
			const pricing = action.payload.price;
			const portIndex = state.selectedPortIndex ?? 0;
			const existingPort = state.ports[portIndex];
			// Preserve the current crossConnect value; if undefined, default to "requireXC"
			const currentCrossConnect = existingPort?.configuration?.crossConnect || "requireXC";
			state.ports[portIndex] = {
			...existingPort,
			configuration: {
			...existingPort?.configuration,
			crossConnect: currentCrossConnect,
			crossConnectPricing: pricing,
			},
		};
		});
		builder.addCase(fetchPortsAndLags.fulfilled, (state, action: PayloadAction<{ ports: AvailablePort[]; lags: AvailableLag[] }>) => {
			state.availablePorts = action.payload.ports;
			state.availableLags = action.payload.lags;
		});
	},
});

export const selectLocations = (state: RootState) => state.order.locations;
export const selectPricingPlans = (state: RootState) => state.order.pricingPlans;
export const selectAvailablePorts = (state: RootState) => state.order.availablePorts;
export const selectAvailableLags = (state: RootState) => state.order.availableLags;

export const {
	updateOrder,
	addNewPort,
	removePort,
	resetOrder,
	loadSavedOrder,
	setSelectedPort,
	setCurrentStep,
	setPricingPlans,
	setCrossConnectPricing,

} = orderSlice.actions;
export default orderSlice.reducer;
